import type { ReactNode } from 'react';
import styles from './Card.module.css';

/** §5's Card/Panel tones. `default` is the plain card; the other three are the §2 tints. */
export const CARD_TONES = ['default', 'notice', 'positive', 'danger'] as const;
export type CardTone = (typeof CARD_TONES)[number];

export interface CardProps {
    readonly tone?: CardTone;
    readonly children: ReactNode;
    readonly 'data-testid'?: string;
}

/**
 * The §5 Card/Panel surface — `surface/card` on `border/card` at `radius/lg`, padded `space/6`.
 *
 * A shell and nothing more: no title prop, no header slot. Both were considered and left out under
 * §0's rule of two — no screen consumes a card this slice, so a header API invented now would be
 * guessing at screens nobody has drawn (§8 still lists most of them as to-design).
 *
 * Known, and deliberate: `shadow/card` does almost nothing here. Under Empire it is a 4%-alpha
 * green-black at a 1px offset — visible on white, effectively invisible on `surface/canvas`, which
 * is the surface a card actually sits on. Measured at subtask 4 and recorded there. It is faithful
 * to the prototype, and `border/card` is what is really separating the card from the canvas; the
 * shadow stays because §5 specifies it, not because it is doing work. Worth knowing before anyone
 * adds a second shadow wondering why the first one is missing.
 */
export function Card({ tone = 'default', children, 'data-testid': testId }: CardProps) {
    return (
        <div className={`${styles.card} ${styles[tone]}`} data-testid={testId}>
            {children}
        </div>
    );
}
